import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { downloadWorkspaceData, requestAccountDeletion, cancelAccountDeletion } from '../../api';
import api from '../../api';

// Admin GDPR controls: export all workspace data, and request account deletion.
export default function PrivacySettings() {
  const { user } = useAuth();
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [deletionAt, setDeletionAt] = useState(user?.workspace?.deletion_requested_at || null);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Refresh workspace to reflect any existing deletion request.
  useEffect(() => {
    api.get('/workspace').then(r => setDeletionAt(r.data?.deletion_requested_at || null)).catch(() => {});
  }, []);

  const doExport = async () => {
    setMsg(''); setBusy('export');
    try { await downloadWorkspaceData(); setMsg('Your export has been downloaded.'); }
    catch { setMsg('Export failed. Please try again.'); }
    setBusy('');
  };

  const doRequestDeletion = async () => {
    setMsg(''); setBusy('delete');
    try {
      const res = await requestAccountDeletion();
      setDeletionAt(res.deletion_requested_at);
      setShowConfirm(false); setConfirmText('');
      setMsg('Deletion request submitted. Our team will process it within 30 days.');
    } catch { setMsg('Could not submit the request. Please contact support.'); }
    setBusy('');
  };

  const doCancelDeletion = async () => {
    setMsg(''); setBusy('cancel');
    try { await cancelAccountDeletion(); setDeletionAt(null); setMsg('Deletion request cancelled.'); }
    catch { setMsg('Could not cancel. Please try again.'); }
    setBusy('');
  };

  return (
    <div style={{ maxWidth: 620 }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 6 }}>Privacy &amp; Data</h2>
      <p style={{ color: 'var(--muted)', marginBottom: 24, fontSize: '0.9rem' }}>
        Manage your workspace data in line with GDPR — export everything you hold, or request erasure.
      </p>

      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: '0.875rem', background: 'var(--glass-1, #f0f9ff)', color: 'var(--text)', border: '1px solid var(--hairline, #bae6fd)' }}>{msg}</div>
      )}

      {/* Export */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Export workspace data</div>
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 14 }}>
          Download a machine-readable JSON file with all employees, time entries, screenshots metadata, activity, teams, leave and audit records for this workspace.
        </div>
        <button className="btn btn-primary" onClick={doExport} disabled={busy === 'export'}>
          {busy === 'export' ? 'Preparing…' : 'Download export (JSON)'}
        </button>
      </div>

      {/* Deletion */}
      <div className="card" style={{ borderColor: '#fecaca' }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: '#b91c1c' }}>Delete account &amp; data</div>
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 14 }}>
          Request permanent deletion of this workspace and all associated data. Your account stays active until we process the request (within 30 days). This cannot be undone once executed.
        </div>

        {deletionAt ? (
          <div>
            <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: '0.875rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
              ⚠️ Deletion requested on {new Date(deletionAt).toLocaleDateString()}. It will be processed within 30 days.
            </div>
            <button className="btn" onClick={doCancelDeletion} disabled={busy === 'cancel'}>
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel deletion request'}
            </button>
          </div>
        ) : !showConfirm ? (
          <button className="btn" style={{ color: '#b91c1c', borderColor: '#b91c1c' }} onClick={() => setShowConfirm(true)}>
            Request account deletion
          </button>
        ) : (
          <div>
            <div className="form-group">
              <label>Type <strong>DELETE</strong> to confirm</label>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" autoFocus />
            </div>
            <button className="btn btn-primary" style={{ background: '#dc2626' }} disabled={confirmText !== 'DELETE' || busy === 'delete'} onClick={doRequestDeletion}>
              {busy === 'delete' ? 'Submitting…' : 'Confirm deletion request'}
            </button>
            <button className="btn" style={{ marginLeft: 8 }} onClick={() => { setShowConfirm(false); setConfirmText(''); }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
